// @givmo/board — the running to-do list's words (CBG-UX7-WEB-DIR, CBG-UX7-WEB-OFF; design stop
// design-268's §4, §7 and §9, frames d1–d5, o1–o4).
//
// ══ WHICH WORDS ARE APPROVED ════════════════════════════════════════════════════════════════════
//
// All of them. Jonathan approved the screens for building with every new word a draft
// (D-CBG-35, 2026-10-22 23:09 PDT, "looks good"), and the words, every row as recommended, in the
// wayfinding copy round (2026-10-24 11:11 PDT, "yes all 3 and i agree with your recs";
// `scratchpad/2026-10-24-wayfinding-copy-round/copy-review.html`). `emptyDirector`, the director's
// list's empty line rewritten for D-CBG-40's first visit, and the lines naming the yearly form
// ("conflicts of interest form", "fill out") were approved in CBG-UX-COPY2 (D-CBG-42, 2026-10-24
// 19:08 PDT), with "Open your whole checklist" and "I’ve done this". Each line names the frame that
// draws it, or "not drawn". A line that reuses an approved one says so and imports it, so the two never
// drift: the retired card's heading, the member's own list's line, the officers' panel's steps, and
// the Meetings panel's sentence about the Form 990 date (`../meetings/form990-date-words.ts`).
//
// "Derived" marks a line §9 did not write out: Completed's past tense of a to-do §9 lists only in its
// present tense (§4.4: "Each reads as its to-do did, in the past tense"), and a singular §9 gives only
// in the plural. The round approved them with the rest. One row stays as drafted, by choice: the note
// above `doneOn`.
//
// A NAME AFTER "THE" (rows 6.7–6.9 and their past tenses): only Board's own words read in the
// sentence's case — a document named by its kind, and the prefix of the minutes' default title
// (`MINUTES_TITLE_PREFIXES`). A title the board wrote keeps its capitals, less its own leading "The ".
//
// ⚠ The list's discipline (§4.3): a date appears only where the record has one (a due date the board
// set, a meeting's day, the date the Form 990 instructions use); nothing says late, overdue or urgent;
// nothing counts what someone else hasn't done; and a written consent is named as its email names it,
// never by its title, text or date (CBG-FIX107).

import { coiWords } from '../conflicts/coi-words';
import { form990Contemporaneous } from '../meetings/form990-date-words';
import type { DocumentName } from '../onboarding/onboarding-decode';
import { onboardingWords } from '../onboarding/onboarding-words';

/** Board's own words in the sentence's case: "Bylaws" → "bylaws"; "Conflict of interest policy" →
 *  "conflict of interest policy". */
function lowerFirst(text: string): string {
  return text.length === 0 ? text : text.charAt(0).toLowerCase() + text.slice(1);
}

/** A title the board wrote, after "the": as written, less its own leading "The " ("The Annual
 *  Meeting" → "Annual Meeting"). */
function boardTitle(title: string): string {
  const trimmed = title.trim();
  const rest = trimmed.replace(/^the\s+/i, '');
  return rest.length === 0 ? trimmed : rest;
}

/** Board's default minutes titles' prefixes (backend `board_minutes_titles.py`). */
const MINUTES_TITLE_PREFIXES = ['Minutes — ', 'Executive session — '];

/** Minutes after "the": a default title's prefix in the sentence's case, the meeting's title after it
 *  as the board wrote it ("minutes — November Board Meeting"); a title of the board's own, as
 *  written. */
function minutesInSentence(title: string): string {
  const trimmed = title.trim();
  const prefix = MINUTES_TITLE_PREFIXES.find((candidate) => trimmed.startsWith(candidate));
  return prefix === undefined
    ? boardTitle(trimmed)
    : `${lowerFirst(prefix)}${trimmed.slice(prefix.length)}`;
}

export const todoWords = {
  // ── The list itself (every frame) ───────────────────────────────────────────────────────────────

  // Drawn d1–d5, o1–o4: the title and the minimized tab (Jonathan's word).
  title: 'To-do list',
  // Drawn d1–d5, o1–o4: the two tabs ("Completed" is Jonathan's word).
  tabToDo: 'To do',
  tabCompleted: 'Completed',
  // Not drawn: the minimize button's accessible name.
  minimize: 'Minimize the to-do list',
  // Not drawn: the minimized tab's accessible name, with its count.
  tabName: (count: number) =>
    count === 0 ? 'To-do list, nothing to do' : `To-do list, ${count} to do`,

  // ── Groups (frame d5) ───────────────────────────────────────────────────────────────────────────

  // Drawn d5: CBG-UX6's name for the member's own list.
  checklistGroup: 'New member checklist',
  // Approved: the member's own list's line (CBG-YR4-START, g5).
  checklistLine: onboardingWords.toReadLine,
  // Drawn d5: the way to the member's whole list (CBG-UX-COPY2's A5e).
  openChecklist: 'Open your whole checklist',
  // Approved: the retired card's heading (CBG-FIX109).
  waitingGroup: coiWords.waitingOnYou,

  // ── A director's items (frames d1, d5) ──────────────────────────────────────────────────────────

  // Drawn d5: one document on the checklist, by its kind in the sentence's case ("Read the bylaws"),
  // or by the title the board wrote, as written (6.7).
  readDocument: ({ name, byKind }: DocumentName) =>
    `Read the ${byKind ? lowerFirst(name.trim()) : boardTitle(name)}`,
  // Approved: the member's own list's act on a step (CBG-YR4-START; "I’ve done this", A5d).
  markDone: onboardingWords.markOwnStepDone,
  // Drawn d1 and d5: a meeting to answer for, by the title the board wrote (6.8), and by when.
  replyToMeeting: (meetingTitle: string | null) =>
    `Say whether you’ll be at the ${boardTitle(meetingTitle ?? '') || 'meeting'}`,
  beforeTheMeeting: (when: string) => `Before ${when}`,
  // Not drawn: minutes to sign (6.9).
  signMinutes: (minutesTitle: string) => `Sign the ${minutesInSentence(minutesTitle)}`,
  // Drawn d1: the year's conflicts of interest form, and the board's self-assessment.
  answerDisclosure: (label: string) => `Fill out your ${label} conflicts of interest form`,
  answerAssessment: (label: string) => `Answer the board’s ${label} self-assessment`,
  // Drawn d1: a copy of the return, and the day it was provided (context, not a due date).
  readReturn: (year: number, form: string) => `Read the ${year} Form ${form}`,
  /** EMC-WEB4 (frame 25): the ask's line says what it asks, in the ways it offers. */
  boardAsk: (campaign: string, share: boolean, page: boolean) =>
    share && page
      ? `${campaign}: share it, or make your own fundraiser page`
      : share
        ? `${campaign}: share it`
        : `${campaign}: make your own fundraiser page`,
  actedOnBoardAsk: (campaign: string) => `Helped with ${campaign}`,
  dueOn: (day: string) => `Due ${day}`,
  providedOn: (day: string) => `Provided ${day}`,

  // ── An officer's items (frame o1) ───────────────────────────────────────────────────────────────

  // Approved: the officers' panel's two steps (CBG-YR4-START, g3).
  sendQuestionnaire: onboardingWords.sendQuestionnaireNamed,
  chooseBuddy: onboardingWords.chooseBuddy,
  // Drawn o1: who the step is for. Derived: with no day recorded, the name and "new member" alone.
  newMember: (name: string, day: string | null) =>
    day === null ? `${name}, new member` : `${name}, new member · joined ${day}`,
  // Drawn o1: the minutes, the meeting and its day, and by when.
  writeMinutes: 'Write the minutes',
  sendMinutes: 'Send the minutes for approval',
  meetingAndDate: (meeting: string, day: string) => `${meeting}, ${day}`,
  // Drawn o1. "By", not "Due": the date is the Form 990 instructions' description of contemporaneous
  // minutes, never a deadline anyone set (the contemporaneity clock never says the organization must
  // do anything by it); it used to read "Form 990 date: {day}".
  form990Date: (day: string) => `By ${day}, for the Form 990`,
  // Drawn o1: the link at the list's foot, and the Meetings panel's sentence it opens ("Writing
  // things up"), in the committee form while any committee's minutes are listed (7.6).
  form990Link: 'About the Form 990 date',
  form990Lines: (anyCommittee: boolean) => [form990Contemporaneous(anyCommittee)],
  // Drawn o1: a record to approve, named by its title (6.16).
  approveRecord: 'Review a record awaiting approval',
  /** EMC-WEB4 (frame 22): "Approve", never "sign off" (Jonathan, 2026-10-31). */
  approveBoardAsk: (campaign: string) => `Approve the ${campaign} Board Ask`,
  boardAskFrom: (name: string) => `From ${name}`,
  // Drawn o1 (the plural). Derived: the singular.
  reviewMembers: (count: number) =>
    count === 1 ? 'Review a proposed member' : `Review ${count} proposed members`,
  reviewRecords: (count: number) =>
    count === 1 ? 'Review a past record' : `Review ${count} past records`,
  // Drawn o1: who proposed them, and when; "it" for one past record (6.18). The AI that acted is "an
  // AI assistant", never its tool's name (D-CBG-45 as amended, 2026-10-29 08:57 PDT).
  proposedBy: (day: string, oneRecord = false) =>
    `An AI assistant proposed ${oneRecord ? 'it' : 'them'} on ${day}`,
  // Drawn o1–o3: for an officer who is also on the board.
  alsoOnTheBoard:
    'You’re on the board too. What waits for your own signature or answer is on your board page.',
  openBoardPage: 'Open your board page',

  // ── Completed (frames d3, o3) ───────────────────────────────────────────────────────────────────

  // Drawn d3 and o3: the tab's one line.
  completedLine: 'What’s done in the last 90 days.',
  // Drawn d3 and o3: when it was done ("Confirmed" is the member's own list's word), and by whom
  // when it wasn't the reader.
  //
  // Left as drafted (CBG-UX-COPY1's row 7.7, by Jonathan's choice): a completion whose actor's name
  // won't resolve reads "Done {day}" alone, which says nothing false. Saying someone whose name isn't
  // shown did it needs a fact the backend doesn't serve: whether an actor was recorded at all.
  doneOn: (day: string) => `Done ${day}`,
  signedOn: (day: string) => `Signed ${day}`,
  answeredOn: (day: string) => `Answered ${day}`,
  confirmedOn: onboardingWords.confirmedOn,
  doneBy: (when: string, name: string) => `${when} by ${name}`,
  // Drawn d3: a director's acts.
  signedConsent: 'Signed a written consent',
  saidAttending: 'Said you’d be at the meeting',
  // Derived: the meeting's other two answers.
  saidNotAttending: 'Said you won’t be at the meeting',
  saidMaybe: 'Said you might be at the meeting',
  // Derived: the past tense of "Sign the {minutes}", RC10's row, the questionnaire, the
  // self-assessment and the copy of the return.
  signedMinutes: (minutesTitle: string) => `Signed the ${minutesInSentence(minutesTitle)}`,
  signedToApprove: (closedMinutes: string) => `Signed to approve ${closedMinutes}`,
  answeredDisclosure: (label: string) => `Filled out your ${label} conflicts of interest form`,
  answeredAssessment: (label: string) => `Answered the board’s ${label} self-assessment`,
  readTheReturn: (year: number, form: string) => `Read the ${year} Form ${form}`,
  // Drawn o3: an officer's acts.
  sentMinutes: 'Sent the minutes for approval',
  importedRecords: (count: number) =>
    count === 1 ? 'Imported a past record' : `Imported ${count} past records`,
  addedProposedMember: 'Added a proposed member',
  choseBuddy: 'Chose a board buddy',
  // Drawn o3: who proposed the records imported; "it" for one (6.24).
  proposedThem: (oneRecord = false) => `An AI assistant proposed ${oneRecord ? 'it' : 'them'}`,
  // Derived: the past tense of "Approve a record", a proposed member left out, and the
  // questionnaire sent.
  approvedRecord: 'Approved a record',
  sentBoardAsk: 'Approved and sent a Board Ask',
  discardedProposedMember: 'Discarded a proposed member',
  sentQuestionnaire: (label: string) => `Sent the ${label} conflicts of interest form`,

  // ── Empty (frames d4, o4) ───────────────────────────────────────────────────────────────────────

  // Drawn d4 and o4.
  emptyTitle: 'Nothing to do right now',
  // Approved (CBG-UX-COPY2's A5g), drawn c1b: what the list is and what will show up on it, since a
  // director's first visit opens it empty (D-CBG-40's second answer). "Written consent", never
  // "UWC"; "yearly conflicts of interest form", Jonathan's word for the item (D-CBG-42).
  emptyDirector:
    'This is your to-do list for this board. Anything you owe the board, like signing a written consent or filling out your yearly conflicts of interest form, will show up here when it’s assigned to you.',
  emptyOfficer:
    'When something needs an officer — minutes to write or send, members or records to review — it shows up here.',
  // Not drawn: Completed with nothing in it.
  emptyCompleted: 'Nothing completed in the last 90 days.',

  // ── Loading and failure (not drawn) ─────────────────────────────────────────────────────────────

  // Not drawn: the whole list's failure, and Completed's own (7.8), which names what it couldn't
  // load while To do still shows.
  loadFailed: 'We couldn’t load your to-do list.',
  completedLoadFailed: 'We couldn’t load what’s done in the last 90 days.',
  tryAgain: 'Try again',
} as const;
